import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle, XCircle, UserX, UserCheck, Search,
  Clock, Users, UserPlus, UserCog, Images as ImagesIcon, ImageOff,
  ChevronUp, ChevronDown, ChevronsUpDown, KeyRound, Copy, RefreshCw, Download,
} from 'lucide-react';
import {
  getUsers, approveUser, rejectUser, disableUser, createUser, setUserRole, setUserGalleryUpload,
  getSupervisorCode, setSupervisorCode,
} from '../services/api';
import { UserEmail } from '../components/common/UserEmail';
import { roleColorClasses, roleLabel } from '../utils/roleStyles';

type Role = 'enumerator' | 'transcriber' | 'supervisor' | 'sub-admin' | 'admin';

type Tab = 'pending' | 'all';

type SortField = 'name' | 'email' | 'role' | 'status' | 'supervisor' | 'createdAt';
type SortDir = 'asc' | 'desc';

// Dates default newest-first; text columns default A→Z on first click.
function defaultDirForField(f: SortField): SortDir {
  return f === 'createdAt' ? 'desc' : 'asc';
}

interface User {
  userId: string;
  displayName: string;
  email: string;
  phone?: string;
  role: string;
  approvalStatus: string;
  supervisorName?: string;
  galleryUploadAllowed?: boolean;
  createdAt: string;
}

function ApprovalStatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    pending: 'badge-yellow',
    approved: 'badge-green',
    rejected: 'badge-red',
    disabled: 'badge-gray',
  };
  return <span className={map[status] ?? 'badge-gray'}>{status}</span>;
}

function RoleBadge({ role }: { role: string }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold ${roleColorClasses(role)}`}
    >
      {roleLabel(role)}
    </span>
  );
}

function SortableTh({
  label,
  field,
  sort,
  onSort,
}: {
  label: string;
  field: SortField;
  sort: { field: SortField; dir: SortDir };
  onSort: (field: SortField) => void;
}) {
  const active = sort.field === field;
  const Icon = !active ? ChevronsUpDown : sort.dir === 'desc' ? ChevronDown : ChevronUp;
  return (
    <th
      onClick={() => onSort(field)}
      className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700"
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <Icon size={12} className={active ? 'text-violet-600' : 'text-gray-400'} />
      </span>
    </th>
  );
}

interface CreateUserForm {
  displayName: string;
  email: string;
  password: string;
  role: Role;
  phone: string;
}

const emptyCreateForm: CreateUserForm = {
  displayName: '',
  email: '',
  password: '',
  role: 'enumerator',
  phone: '',
};

// ─── CSV export helpers ─────────────────────────────────────────────────────

/// Columns exported in the CSV. The order here drives both the header row
/// and each per-user row, so changes only need to happen in one place.
const CSV_COLUMNS: Array<{ header: string; pick: (u: Record<string, unknown>) => unknown }> = [
  { header: 'User ID',         pick: (u) => u.userId },
  { header: 'Display Name',    pick: (u) => u.displayName },
  { header: 'Email',           pick: (u) => u.email },
  { header: 'Phone',           pick: (u) => u.phone },
  { header: 'Role',            pick: (u) => u.role },
  { header: 'Approval Status', pick: (u) => u.approvalStatus },
  { header: 'Supervisor ID',   pick: (u) => u.supervisorId },
  { header: 'Supervisor Name', pick: (u) => u.supervisorName },
  { header: 'Rejection Reason', pick: (u) => u.rejectionReason },
  { header: 'Organization',    pick: (u) => (u.registrationDetails as Record<string, unknown> | undefined)?.organizationName },
  { header: 'District',        pick: (u) => (u.registrationDetails as Record<string, unknown> | undefined)?.district },
  { header: 'State',           pick: (u) => (u.registrationDetails as Record<string, unknown> | undefined)?.state },
  { header: 'Device Model',    pick: (u) => (u.deviceInfo as Record<string, unknown> | undefined)?.model },
  { header: 'Device OS',       pick: (u) => (u.deviceInfo as Record<string, unknown> | undefined)?.os },
  { header: 'App Version',     pick: (u) => (u.deviceInfo as Record<string, unknown> | undefined)?.appVersion },
  { header: 'Gallery Upload Allowed', pick: (u) => u.galleryUploadAllowed },
  { header: 'Session Count',   pick: (u) => u.sessionCount },
  { header: 'Last Active At',  pick: (u) => u.lastActiveAt },
  { header: 'Created At',      pick: (u) => u.createdAt },
  { header: 'Updated At',      pick: (u) => u.updatedAt },
];

/// Per-RFC-4180: wrap any value containing a comma, double-quote, CR, or LF
/// in double quotes; escape internal double quotes by doubling them.
function csvEscape(value: unknown): string {
  if (value == null) return '';
  const s = typeof value === 'string' ? value : String(value);
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function usersToCsv(users: Array<Record<string, unknown>>): string {
  const header = CSV_COLUMNS.map((c) => csvEscape(c.header)).join(',');
  const rows = users.map((u) =>
    CSV_COLUMNS.map((c) => csvEscape(c.pick(u))).join(','),
  );
  // Lead with a UTF-8 BOM so Excel opens it with the right encoding —
  // Excel on Windows defaults to ANSI without one.
  return '﻿' + [header, ...rows].join('\r\n');
}

function downloadCsv(filename: string, csv: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Defer revoking so the browser has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function UsersPage() {
  const [tab, setTab] = useState<Tab>('pending');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ field: SortField; dir: SortDir }>({
    field: 'createdAt',
    dir: 'desc',
  });
  const toggleSort = (field: SortField) =>
    setSort((prev) =>
      prev.field === field
        ? { field, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
        : { field, dir: defaultDirForField(field) },
    );
  const [rejectModal, setRejectModal] = useState<{ userId: string; name: string } | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [approveModal, setApproveModal] = useState<{ userId: string; name: string } | null>(null);
  const [approveRole, setApproveRole] = useState<'enumerator' | 'transcriber' | 'supervisor'>('enumerator');
  const [roleModal, setRoleModal] = useState<{ userId: string; name: string; currentRole: Role } | null>(null);
  const [roleDraft, setRoleDraft] = useState<Role>('enumerator');
  const [roleError, setRoleError] = useState<string | null>(null);
  const [createModal, setCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState<CreateUserForm>(emptyCreateForm);
  // CSV export is driven by an on-demand fetch so it's fresh regardless of
  // which tab is active when the admin clicks Export.
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const handleExportCsv = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const res = await getUsers({ limit: '5000' });
      const users = (res?.users ?? []) as Array<Record<string, unknown>>;
      if (users.length === 0) {
        setExportError('No users to export.');
        return;
      }
      const today = new Date();
      const stamp = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      downloadCsv(`dataone-users-${stamp}.csv`, usersToCsv(users));
    } catch (e) {
      setExportError(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };
  const [createError, setCreateError] = useState<string | null>(null);
  const [codeModal, setCodeModal] = useState<{ userId: string; name: string } | null>(null);

  const qc = useQueryClient();

  const { data: pendingData, isLoading: loadingPending } = useQuery({
    queryKey: ['users-pending'],
    // Bump past the default 50-row cap so the Pending tab actually shows
    // every awaiting-approval user, not just the most recent page.
    queryFn: () => getUsers({ approvalStatus: 'pending', limit: '5000' }),
    enabled: tab === 'pending',
  });

  const { data: allData, isLoading: loadingAll } = useQuery({
    queryKey: ['users-all'],
    // Same cap fix — the All Users tab and the CSV export both rely on
    // this list, and showing only 52 of N total was the long-standing bug.
    queryFn: () => getUsers({ limit: '5000' }),
    enabled: tab === 'all',
  });

  const approveMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      approveUser(userId, role),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      setApproveModal(null);
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ userId, reason }: { userId: string; reason: string }) =>
      rejectUser(userId, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      setRejectModal(null);
      setRejectReason('');
    },
  });

  const disableMutation = useMutation({
    mutationFn: ({ userId, action }: { userId: string; action: 'disable' | 'enable' }) =>
      disableUser(userId, action),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  const [galleryToast, setGalleryToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const galleryMutation = useMutation({
    mutationFn: ({ userId, allowed }: { userId: string; allowed: boolean }) =>
      setUserGalleryUpload(userId, allowed),
    onSuccess: (_, { allowed }) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      setGalleryToast({
        type: 'success',
        message: allowed
          ? 'Gallery upload enabled. User signed out — must log in again to see the change.'
          : 'Gallery upload disabled. User signed out — must log in again to see the change.',
      });
      setTimeout(() => setGalleryToast(null), 4000);
    },
    onError: (err: Error) => {
      setGalleryToast({ type: 'error', message: err.message ?? 'Failed to update gallery permission.' });
      setTimeout(() => setGalleryToast(null), 6000);
    },
  });

  const roleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: Role }) =>
      setUserRole(userId, role),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      setRoleModal(null);
      setRoleError(null);
    },
    onError: (err: Error) => setRoleError(err.message),
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateUserForm) => createUser(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      setCreateModal(false);
      setCreateForm(emptyCreateForm);
      setCreateError(null);
    },
    onError: (err: Error) => setCreateError(err.message),
  });

  const users: User[] =
    tab === 'pending'
      ? (pendingData?.users ?? [])
      : (allData?.users ?? []);

  const filtered = users.filter(
    (u) =>
      u.displayName.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase()),
  );

  const sorted = useMemo(() => {
    const factor = sort.dir === 'desc' ? -1 : 1;
    const arr = [...filtered];
    arr.sort((a, b) => {
      let cmp = 0;
      switch (sort.field) {
        case 'name':
          cmp = a.displayName.localeCompare(b.displayName);
          break;
        case 'email':
          cmp = a.email.localeCompare(b.email);
          break;
        case 'role':
          cmp = (a.role ?? '').localeCompare(b.role ?? '');
          break;
        case 'status':
          cmp = (a.approvalStatus ?? '').localeCompare(b.approvalStatus ?? '');
          break;
        case 'supervisor':
          cmp = (a.supervisorName ?? '').localeCompare(b.supervisorName ?? '');
          break;
        case 'createdAt':
          cmp = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
      }
      if (cmp === 0) cmp = a.displayName.localeCompare(b.displayName);
      return cmp * factor;
    });
    return arr;
  }, [filtered, sort]);

  const loading = tab === 'pending' ? loadingPending : loadingAll;

  return (
    <div className="space-y-6">
      {/* Toast — feedback for the gallery-upload toggle. */}
      {galleryToast && (
        <div
          className={`fixed top-4 right-4 z-50 px-4 py-2.5 rounded-xl shadow-lg text-sm font-medium max-w-md ${
            galleryToast.type === 'success'
              ? 'bg-green-600 text-white'
              : 'bg-red-600 text-white'
          }`}
        >
          {galleryToast.message}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users</h1>
          <p className="text-gray-500 text-sm mt-1">Manage enumerator and supervisor accounts</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportCsv}
            disabled={exporting}
            title={exportError ?? 'Download all users as CSV'}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg
                       border border-gray-300 bg-white text-gray-700 hover:bg-gray-50
                       disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            <Download size={16} />
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
          <button
            className="btn-primary flex items-center gap-2"
            onClick={() => { setCreateModal(true); setCreateError(null); }}
          >
            <UserPlus size={16} />
            Create User
          </button>
        </div>
      </div>
      {exportError && (
        <div className="px-3 py-2 rounded-md bg-red-50 text-red-700 text-sm border border-red-200">
          Export failed: {exportError}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        {(['pending', 'all'] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-2 ${
              tab === t ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t === 'pending' ? <Clock size={14} /> : <Users size={14} />}
            {t === 'pending' ? 'Pending Approvals' : 'All Users'}
            {t === 'pending' && pendingData?.count > 0 && (
              <span className="bg-amber-100 text-amber-700 text-xs px-1.5 py-0.5 rounded-full">
                {pendingData.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          className="input pl-9"
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableTh label="Name" field="name" sort={sort} onSort={toggleSort} />
                <SortableTh label="Email" field="email" sort={sort} onSort={toggleSort} />
                <SortableTh label="Role" field="role" sort={sort} onSort={toggleSort} />
                <SortableTh label="Status" field="status" sort={sort} onSort={toggleSort} />
                <SortableTh label="Supervisor" field="supervisor" sort={sort} onSort={toggleSort} />
                <SortableTh label="Registered" field="createdAt" sort={sort} onSort={toggleSort} />
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-400 text-sm">
                    Loading…
                  </td>
                </tr>
              ) : sorted.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-400 text-sm">
                    {tab === 'pending' ? 'No pending approvals' : 'No users found'}
                  </td>
                </tr>
              ) : (
                sorted.map((user) => (
                  <tr key={user.userId} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900 text-sm">{user.displayName}</p>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <UserEmail email={user.email} role={user.role} />
                    </td>
                    <td className="px-4 py-3">
                      <RoleBadge role={user.role} />
                    </td>
                    <td className="px-4 py-3">
                      <ApprovalStatusBadge status={user.approvalStatus} />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {user.supervisorName ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {user.approvalStatus === 'pending' && (
                          <>
                            <button
                              onClick={() =>
                                setApproveModal({ userId: user.userId, name: user.displayName })
                              }
                              className="text-emerald-600 hover:text-emerald-800 p-1 rounded"
                              title="Approve"
                            >
                              <CheckCircle size={18} />
                            </button>
                            <button
                              onClick={() =>
                                setRejectModal({ userId: user.userId, name: user.displayName })
                              }
                              className="text-red-500 hover:text-red-700 p-1 rounded"
                              title="Reject"
                            >
                              <XCircle size={18} />
                            </button>
                          </>
                        )}
                        {user.approvalStatus === 'approved' && (
                          <>
                            <button
                              onClick={() => {
                                setRoleModal({
                                  userId: user.userId,
                                  name: user.displayName,
                                  currentRole: user.role as Role,
                                });
                                setRoleDraft(user.role as Role);
                                setRoleError(null);
                              }}
                              className="text-gray-400 hover:text-violet-600 p-1 rounded"
                              title="Change role"
                            >
                              <UserCog size={18} />
                            </button>
                            {user.role === 'supervisor' && (
                              <button
                                onClick={() =>
                                  setCodeModal({ userId: user.userId, name: user.displayName })
                                }
                                className="text-gray-400 hover:text-indigo-600 p-1 rounded"
                                title="View / regenerate supervisor code"
                              >
                                <KeyRound size={18} />
                              </button>
                            )}
                            <button
                              onClick={() => galleryMutation.mutate({
                                userId: user.userId,
                                allowed: !user.galleryUploadAllowed,
                              })}
                              disabled={
                                galleryMutation.isPending &&
                                galleryMutation.variables?.userId === user.userId
                              }
                              className={
                                user.galleryUploadAllowed
                                  ? 'text-violet-600 hover:text-violet-800 p-1 rounded disabled:opacity-50'
                                  : 'text-gray-400 hover:text-violet-600 p-1 rounded disabled:opacity-50'
                              }
                              title={
                                user.galleryUploadAllowed
                                  ? 'Gallery upload ENABLED — click to disable. User will be signed out.'
                                  : 'Gallery upload DISABLED — click to enable. User will be signed out.'
                              }
                            >
                              {user.galleryUploadAllowed ? <ImagesIcon size={18} /> : <ImageOff size={18} />}
                            </button>
                            <button
                              onClick={() => disableMutation.mutate({ userId: user.userId, action: 'disable' })}
                              className="text-gray-400 hover:text-red-500 p-1 rounded"
                              title="Disable"
                            >
                              <UserX size={18} />
                            </button>
                          </>
                        )}
                        {user.approvalStatus === 'disabled' && (
                          <button
                            onClick={() => disableMutation.mutate({ userId: user.userId, action: 'enable' })}
                            className="text-gray-400 hover:text-emerald-500 p-1 rounded"
                            title="Re-enable"
                          >
                            <UserCheck size={18} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Approve Modal */}
      {approveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Approve User</h3>
            <p className="text-sm text-gray-500 mb-4">
              Approve <strong>{approveModal.name}</strong> and assign a role.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select
              className="input mb-4"
              value={approveRole}
              onChange={(e) => setApproveRole(e.target.value as 'enumerator' | 'transcriber' | 'supervisor')}
            >
              <option value="enumerator">Enumerator</option>
              <option value="transcriber">Transcriber</option>
              <option value="supervisor">Supervisor</option>
            </select>
            <div className="flex gap-3">
              <button
                className="btn-primary flex-1"
                onClick={() =>
                  approveMutation.mutate({ userId: approveModal.userId, role: approveRole })
                }
                disabled={approveMutation.isPending}
              >
                {approveMutation.isPending ? 'Approving…' : 'Approve'}
              </button>
              <button className="btn-secondary flex-1" onClick={() => setApproveModal(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Role Modal */}
      {roleModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Change Role</h3>
            <p className="text-sm text-gray-500 mb-4">
              Update the role for <strong>{roleModal.name}</strong>. Currently{' '}
              <strong>{roleModal.currentRole}</strong>.
            </p>
            {roleError && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                {roleError}
              </div>
            )}
            <label className="block text-sm font-medium text-gray-700 mb-1">New role</label>
            <select
              className="input mb-4"
              value={roleDraft}
              onChange={(e) => setRoleDraft(e.target.value as Role)}
            >
              <option value="enumerator">Enumerator</option>
              <option value="transcriber">Transcriber</option>
              <option value="supervisor">Supervisor</option>
              <option value="sub-admin">Sub-Admin</option>
              <option value="admin">Admin</option>
            </select>
            {roleDraft !== roleModal.currentRole && (
              <p className="text-xs text-amber-600 mb-3">
                The user will be signed out and will need to log in again before the new role takes effect.
              </p>
            )}
            <div className="flex gap-3">
              <button
                className="btn-primary flex-1"
                onClick={() =>
                  roleMutation.mutate({ userId: roleModal.userId, role: roleDraft })
                }
                disabled={roleMutation.isPending || roleDraft === roleModal.currentRole}
              >
                {roleMutation.isPending ? 'Saving…' : 'Save'}
              </button>
              <button
                className="btn-secondary flex-1"
                onClick={() => { setRoleModal(null); setRoleError(null); }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {rejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Reject Registration</h3>
            <p className="text-sm text-gray-500 mb-4">
              Reject <strong>{rejectModal.name}</strong>. Optionally provide a reason.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
            <textarea
              className="input mb-4 h-24 resize-none"
              placeholder="e.g. Unable to verify credentials..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
            <div className="flex gap-3">
              <button
                className="btn-danger flex-1"
                onClick={() =>
                  rejectMutation.mutate({
                    userId: rejectModal.userId,
                    reason: rejectReason || 'Registration not approved',
                  })
                }
                disabled={rejectMutation.isPending}
              >
                {rejectMutation.isPending ? 'Rejecting…' : 'Reject'}
              </button>
              <button
                className="btn-secondary flex-1"
                onClick={() => {
                  setRejectModal(null);
                  setRejectReason('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {createModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Create User</h3>
            <p className="text-sm text-gray-500 mb-4">
              Create an account directly. The user will be approved and can log in immediately.
            </p>

            {createError && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                {createError}
              </div>
            )}

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  className="input"
                  placeholder="e.g. Priya Sharma"
                  value={createForm.displayName}
                  onChange={(e) => setCreateForm((f) => ({ ...f, displayName: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  className="input"
                  type="email"
                  placeholder="user@example.com"
                  value={createForm.email}
                  onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password <span className="text-red-500">*</span>
                </label>
                <input
                  className="input"
                  type="password"
                  placeholder="Min 8 chars, 1 uppercase, 1 number"
                  value={createForm.password}
                  onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  className="input"
                  value={createForm.role}
                  onChange={(e) =>
                    setCreateForm((f) => ({
                      ...f,
                      role: e.target.value as CreateUserForm['role'],
                    }))
                  }
                >
                  <option value="enumerator">Enumerator</option>
                  <option value="transcriber">Transcriber</option>
                  <option value="supervisor">Supervisor</option>
                  <option value="sub-admin">Sub-Admin</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone (optional)
                </label>
                <input
                  className="input"
                  type="tel"
                  placeholder="10-digit number"
                  value={createForm.phone}
                  onChange={(e) => setCreateForm((f) => ({ ...f, phone: e.target.value }))}
                />
              </div>
            </div>

            <div className="flex gap-3 mt-5">
              <button
                className="btn-primary flex-1"
                onClick={() => createMutation.mutate(createForm)}
                disabled={
                  createMutation.isPending ||
                  !createForm.displayName ||
                  !createForm.email ||
                  !createForm.password
                }
              >
                {createMutation.isPending ? 'Creating…' : 'Create User'}
              </button>
              <button
                className="btn-secondary flex-1"
                onClick={() => {
                  setCreateModal(false);
                  setCreateForm(emptyCreateForm);
                  setCreateError(null);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Supervisor Code Modal */}
      {codeModal && (
        <SupervisorCodeModal
          userId={codeModal.userId}
          displayName={codeModal.name}
          onClose={() => setCodeModal(null)}
        />
      )}
    </div>
  );
}

// ── Supervisor Code Modal ────────────────────────────────────────────────────
// Lazy-fetches the supervisor's current code on open, lets admin copy it, and
// admin-chosen replacement codes (4–20 alphanumeric, auto-uppercased). Setting
// a new code invalidates the previous one immediately — old code stops working.
function SupervisorCodeModal({
  userId,
  displayName,
  onClose,
}: {
  userId: string;
  displayName: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [copied, setCopied] = useState(false);
  const [draft, setDraft] = useState('');
  const [setError, setSetError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['supervisor-code', userId],
    queryFn: () => getSupervisorCode(userId),
  });

  const setMutation = useMutation({
    mutationFn: (code: string) => setSupervisorCode(userId, code),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['supervisor-code', userId] });
      setConfirming(false);
      setDraft('');
      setSetError(null);
    },
    onError: (err: Error) => setSetError(err.message),
  });

  const code: string | null = data?.code ?? null;
  const trimmed = draft.trim().toUpperCase();
  const isValid = /^[A-Z0-9]{4,20}$/.test(trimmed);
  const replacing = !!code;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Supervisor Code</h3>
        <p className="text-sm text-gray-500 mb-4">
          New users registering on mobile under <strong>{displayName}</strong> must enter this code.
        </p>

        {isLoading ? (
          <div className="py-6 text-center text-sm text-gray-400">Loading…</div>
        ) : code ? (
          <>
            <div className="mb-4 p-4 rounded-xl bg-gray-50 border border-gray-200 flex items-center justify-between">
              <code className="font-mono text-lg tracking-widest text-gray-900">{code}</code>
              <button
                onClick={async () => {
                  await navigator.clipboard.writeText(code);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1500);
                }}
                className="text-gray-400 hover:text-violet-600 px-2 py-1 rounded inline-flex items-center gap-1 text-xs font-medium"
              >
                <Copy size={14} />
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            {data.rotatedCount > 0 && (
              <p className="text-xs text-gray-400 mb-3">
                Changed {data.rotatedCount} time{data.rotatedCount === 1 ? '' : 's'}.
              </p>
            )}
          </>
        ) : (
          <div className="mb-4 p-4 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-800">
            No code yet — set one below.
          </div>
        )}

        {confirming ? (
          <>
            <div className="mb-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
              {replacing
                ? `Replace the current code with "${trimmed}"? The old code stops working immediately.`
                : `Set the code to "${trimmed}"?`}
            </div>
            <div className="flex gap-3">
              <button
                className="btn-primary flex-1"
                disabled={setMutation.isPending}
                onClick={() => setMutation.mutate(trimmed)}
              >
                <RefreshCw size={14} className="inline-block mr-1" />
                {setMutation.isPending ? 'Saving…' : 'Yes, save'}
              </button>
              <button
                className="btn-secondary flex-1"
                onClick={() => {
                  setConfirming(false);
                  setSetError(null);
                }}
              >
                Cancel
              </button>
            </div>
            {setError && (
              <p className="text-red-600 text-xs mt-3">{setError}</p>
            )}
          </>
        ) : (
          <>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              {replacing ? 'New code' : 'Code'}
            </label>
            <input
              type="text"
              className="input mb-1 font-mono tracking-widest uppercase"
              placeholder="e.g. AHMED2026"
              value={draft}
              onChange={(e) => setDraft(e.target.value.toUpperCase())}
              maxLength={20}
              autoFocus
            />
            <p className="text-xs text-gray-400 mb-4">
              4–20 alphanumeric characters (A–Z, 0–9). Must be unique across supervisors.
            </p>

            <div className="flex gap-3">
              <button
                className="btn-primary flex-1"
                disabled={!isValid || trimmed === code}
                onClick={() => {
                  setSetError(null);
                  setConfirming(true);
                }}
              >
                {replacing ? 'Replace Code' : 'Set Code'}
              </button>
              <button className="btn-secondary flex-1" onClick={onClose}>
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
